/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import moment from 'moment';
import './_budget-calendar.scss';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { decrypt } from 'shared-components/hooks/useEncryption';
import type { RootState } from 'mainStore';
import StradaLoader from 'shared-components/components/StradaLoader';
import makeStyles from '@mui/styles/makeStyles';
import Header from './components/Header';
import Filters from './components/Filters';
import TableSection from './components/TableSection';
import type { IEventResponse } from './types';

const useStyles = makeStyles({
  parent: {
    // position: 'relative',
    // zIndex: 0,
  },
  backdrop: {
    // position: 'absolute',
    // zIndex: 2,
  },
});

export default function BudgetCalendarContent(): JSX.Element {
  const { buildingId } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const currentWorkspace = useSelector((state: RootState) => state.workspaces.currentWorkspace.currentWorkspace);
  const [date, setDate] = useState<Date>(new Date());
  const [yearly, setYearly] = useState(false);
  const [search, setSearch] = useState< string>('');
  const [assignees, setAssignees] = useState<number[]>([]);
  const [occurrences, setOccurrences] = useState<number[]>([]);
  const [moves, setMoves] = useState<number[]>([]);
  const [createdFilters, setCreatedFilters] = useState<number[]>([]);
  const [projectFiltrs, setProjectFilters] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [newEventOpen, setNewEventOpen] = useState<boolean>(false);

  // functions
  const handleResetFilters = (): void => {
    setDate(new Date());
    setSearch('');
    setAssignees([]);
    setOccurrences([]);
    setMoves([]);
    setCreatedFilters([]);
    setProjectFilters([]);
    setStatusFilter([]);
  };

  const { data: events = [], refetch } = useQuery(['sidesheet/get-events', date, yearly, search, statusFilter, assignees, occurrences, createdFilters, projectFiltrs], async () => axios({
    url: '/api/budget-calendar/event/',
    method: 'GET',
    params: {
      property: Number(decrypt(buildingId)),
      date: moment(date).format('YYYY-MM-DD'),
      search,
      status: statusFilter,
      assignee: assignees,
      occurrence: occurrences,
      is_annual_budget_event: createdFilters,
      tag: projectFiltrs,
    },
  }), {
    select: (res: AxiosResponse<IEventResponse>) => res.data.detail,
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (buildingId !== null) {
      refetch().catch((e) => { throw e; });
    }
  }, [buildingId]);

  const { mutate: createEvent } = useMutation(async (title: string) => axios({
    url: '/api/budget-calendar/event/',
    method: 'POST',
    data: {
      date: moment(date).format('YYYY-MM-DD'),
      month: date.getMonth() + 1,
      property: Number(decrypt(buildingId)),
      title,
      workspace: currentWorkspace.id,
    },
  }), {
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries('sidesheet/get-events')
        .then();
      enqueueSnackbar('Event created successfully');
    },
  });

  return (
    <div className={`bc-container ${classes.parent}`}>
      <StradaLoader open={false} className={classes.backdrop} />
      <Header setDate={setDate} date={date} yearly={yearly} setYearly={setYearly} setNewEventOpen={setNewEventOpen} />
      <Filters
        search={search}
        setSearch={setSearch}
        assignees={assignees}
        setAssignees={setAssignees}
        occurrences={occurrences}
        setOccurrences={setOccurrences}
        moves={moves}
        setMoves={setMoves}
        createdFilters={createdFilters}
        setCreatedFilters={setCreatedFilters}
        projectFiltrs={projectFiltrs}
        setProjectFilters={setProjectFilters}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        handleResetFilters={handleResetFilters}
      />
      <TableSection events={events} newEventOpen={newEventOpen} setNewEventOpen={setNewEventOpen} createEvent={createEvent} setDate={setDate} />
    </div>

  );
}
